import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import FormInput from "../../../../../components/Form/FormInput";
import FormSelect from "../../../../../components/Form/FormSelect";
import FormTextarea from "../../../../../components/Form/TextArea";
import BackButton from "../../../../../components/Button/BackButton";
import { btn } from "../../../../../components/Button/buttonStyles";

const CreateFlatRateReceivable = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();

  const [form, setForm] = useState({
    po_number: "",
    service_id: "",
    language_pair_id: "",
    subtotal: "",
    currency_id: "",
    file_id: "",
    internal_note: "",
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");

  const [services, setServices] = useState([]);
  const [languagePairs, setLanguagePairs] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [files, setFiles] = useState([]);

  const [projectMeta, setProjectMeta] = useState({
    client_id: null,
    client_name: "",
    specialization_name: "",
  });

  const [loading, setLoading] = useState(true);

  /* Fetch initial data */

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setServerError("");

        // First get project details to extract client_id
        const projectRes = await api.get(`/projects/${projectId}`);
        const projectData = projectRes.data.data;
        const clientId = projectData.client_id;

        console.log("Project Data:", projectData);

        // Extract client name with fallback logic
        const clientName =
          projectData.client?.company_name ||
          `${projectData.client?.primary_user?.first_name || ""} ${
            projectData.client?.primary_user?.last_name || ""
          }`.trim() ||
          "—";

        // Extract specialization name
        const specializationName = projectData.specialization?.name || "—";

        setProjectMeta({
          client_id: clientId,
          client_name: clientName,
          specialization_name: specializationName,
        });

        // Fetch dropdowns in parallel
        const dropdownPromises = [
          api.get(`/client-price-list/client/${clientId}/services`).catch(err => {
            console.error("Services API error:", err);
            return { data: { data: [] } };
          }),
          api.get(`/client-price-list/client/${clientId}/language-pairs`).catch(err => {
            console.error("Language pairs API error:", err);
            return { data: { data: [] } };
          }),
          api.get(`/client-price-list/client/${clientId}/currencies`).catch(err => {
            console.error("Currencies API error:", err);
            return { data: { data: [] } };
          }),
          api.get(`/client/${clientId}/input-files`).catch(err => {
            console.error("Files API error:", err);
            return { data: { data: [] } };
          }),
        ];

        const [s, l, c, f] = await Promise.all(dropdownPromises);

        console.log("Services Response:", s.data);
        console.log("Language Pairs Response:", l.data);
        console.log("Currencies Response:", c.data);
        console.log("Files Response:", f.data);

        const servicesData = Array.isArray(s.data.data) ? s.data.data : [];
        const languagePairsData = Array.isArray(l.data.data) ? l.data.data : [];
        const currenciesData = Array.isArray(c.data.data) ? c.data.data : [];
        const filesData = Array.isArray(f.data.data) ? f.data.data : [];

        setServices(servicesData);
        setLanguagePairs(languagePairsData);
        setCurrencies(currenciesData);
        setFiles(filesData);

        console.log("Services State:", servicesData);
        console.log("Language Pairs State:", languagePairsData);
        console.log("Currencies State:", currenciesData);
        console.log("Files State:", filesData);

        setLoading(false);
      } catch (err) {
        console.error("Failed to load dropdown data", err);
        console.error("Error details:", err.response?.data);
        setServerError(
          err.response?.data?.message || "Failed to load form data. Please try again."
        );
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [projectId]);

  /* Dynamic currency filtering */

  useEffect(() => {
    const fetchFilteredCurrencies = async () => {
      if (!projectMeta.client_id) return;

      // Only filter if both service and language pair are selected
      if (!form.service_id && !form.language_pair_id) return;

      try {
        const params = new URLSearchParams();
        if (form.service_id) params.append("service_id", form.service_id);
        if (form.language_pair_id)
          params.append("language_pair_id", form.language_pair_id);

        const res = await api.get(
          `/client-price-list/client/${projectMeta.client_id}/currencies?${params}`
        );

        const currenciesData = Array.isArray(res.data.data) ? res.data.data : [];
        setCurrencies(currenciesData);

        // Reset currency selection if current selection is not in filtered list
        setForm((prev) =>
          prev.currency_id && !currenciesData.some((c) => c.id === Number(prev.currency_id))
            ? { ...prev, currency_id: "" }
            : prev
        );
      } catch (err) {
        console.error("Failed to load filtered currencies", err);
      }
    };

    fetchFilteredCurrencies();
  }, [form.service_id, form.language_pair_id, projectMeta.client_id]);

  /* Validation */

  const validate = () => {
    const e = {};
    if (!form.service_id) e.service_id = "Service is required.";
    if (!form.subtotal) e.subtotal = "Subtotal is required.";
    if (!form.currency_id) e.currency_id = "Currency is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value) {
      setErrors((prev) => ({ ...prev, [name]: "This field is required." }));
    }
  };

  /* Handlers */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
    setServerError("");
    setSuccess("");
  };

  const buildPayload = () => ({
    project_id: Number(projectId),
    po_number: form.po_number || null,
    service_id: Number(form.service_id),
    language_pair_id: form.language_pair_id
      ? Number(form.language_pair_id)
      : null,
    subtotal: Number(form.subtotal),
    currency_id: Number(form.currency_id),
    file_id: form.file_id ? Number(form.file_id) : null,
    internal_note: form.internal_note || null,
  });

  /* Submit */

  const submit = async (clone) => {
    if (!validate()) return;

    try {
      const payload = buildPayload();
      const res = await api.post(
        "/project-finances/flat-rate-receivables",
        payload
      );

      setSuccess(res.data.message || "Receivable created successfully");

      if (!clone) {
        setTimeout(() => navigate(-1), 1500);
      } else {
        // Reset form for cloning, keep same service/language pair/currency
        setForm((prev) => ({
          ...prev,
          po_number: "",
          subtotal: "",
          file_id: "",
          internal_note: "",
        }));
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setServerError(
        err.response?.data?.message || "Failed to create receivable"
      );
    }
  };

  /* UI */

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="flex items-center gap-3 mb-5">
          <BackButton />
          <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
            New Flat Rate Receivable
          </h1>
        </div>

        <form className="bg-white shadow rounded-lg p-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase text-gray-500 font-semibold mb-1">
                Project Client
              </p>
              <p className="text-gray-900 font-medium">
                {projectMeta.client_name}
              </p>
            </div>

            <div>
              <p className="text-xs uppercase text-gray-500 font-semibold mb-1">
                Project Specialization
              </p>
              <p className="text-gray-900 font-medium">
                {projectMeta.specialization_name}
              </p>
            </div>
          </div>

          <hr className="my-6 border-gray-200" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-1">
            <FormInput
              label="PO Number"
              name="po_number"
              value={form.po_number}
              onChange={handleChange}
              error={errors.po_number}
            />

            <FormSelect
              label="Service"
              name="service_id"
              value={form.service_id}
              onChange={handleChange}
              onBlur={handleBlur}
              options={services.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
              error={errors.service_id}
              required
            />

            <FormSelect
              label="Language Pair"
              name="language_pair_id"
              value={form.language_pair_id}
              onChange={handleChange}
              options={languagePairs.map((l) => ({
                value: l.id,
                label: `${l.sourceLanguage?.name || "?"} → ${
                  l.targetLanguage?.name || "?"
                }`,
              }))}
              error={errors.language_pair_id}
            />

            <FormInput
              label="Subtotal"
              name="subtotal"
              type="number"
              step="0.01"
              value={form.subtotal}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.subtotal}
              required
            />

            <FormSelect
              label="Currency"
              name="currency_id"
              value={form.currency_id}
              onChange={handleChange}
              onBlur={handleBlur}
              options={currencies.map((c) => ({
                value: c.id,
                label: `${c.currency?.code || "?"} (${
                  c.currency?.symbol || "?"
                })`,
              }))}
              error={errors.currency_id}
              required
            />

            <FormSelect
              label="File"
              name="file_id"
              value={form.file_id}
              onChange={handleChange}
              options={files.map((f) => ({
                value: f.id,
                label: f.original_file_name || f.file_name,
              }))}
            />

            <div className="md:col-span-2">
              <FormTextarea
                label="Internal Note"
                name="internal_note"
                value={form.internal_note}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={4}
                error={errors.internal_note}
              />
            </div>
          </div>

          <span className="text-gray-500 text-sm mt-1">
            Fields marked with <span className="text-danger-600">*</span> are
            mandatory.
          </span>

          {serverError && (
            <div className="bg-danger-100 text-danger-700 border border-danger-400 rounded p-3 text-center font-semibold">
              {serverError}
            </div>
          )}

          {success && (
            <div className="bg-green-100 text-green-700 border border-green-400 rounded p-3 text-center font-semibold">
              {success}
            </div>
          )}

          <div className="mt-4 space-x-4">
            <button
              type="button"
              onClick={() => submit(false)}
              className={btn("primary", "sm")}
            >
              Create
            </button>

            <button
              type="button"
              onClick={() => submit(true)}
              className={btn("primary", "sm")}
            >
              Create & Clone
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateFlatRateReceivable;
